import dayjs from 'dayjs'
import { IExamResponse, ExamSubtype } from 'types/exam/types'

export interface AnalysisResult {
  result_type: string
  result_value: number // 0 to 1
}

export interface DiagnosticFilters {
  /**
   * Subtipos de raio-x usados no filtro de listagem.
   * Por padrão filtra por cxray_pa/cxray_ap (ver CXRAY_FILTER_SUBTYPES).
   * Editável apenas por usuários admin através do filtro de subtipo.
   */
  subtypes: ExamSubtype[]
  diagnosticTypes: ECGDiagnosticCode[] | XRayDiagnosticCode[] | ['none']
  examDateStart: dayjs.Dayjs | null
  examDateEnd: dayjs.Dayjs | null
  patientId: string
  examId: string
  diagnosticId: string
  status: '0' | '1' | '2' | 'all'
  /**
   * Filtro de Revisão — só Raio-X. Níveis do exame (maior label):
   * '0' Standard/Baixa, '1' Prioritária, '2' Imediata.
   */
  review: string[]
  /**
   * Filtro de Prioridade — só Raio-X. Níveis do exame (maior tier):
   * '0' Baixa, '1' Standard, '2' Prioritária, '3' Imediata.
   */
  priority: string[]
}

export type ExamType = 'ecg' | 'cxray'

/**
 * Configuração que parametriza a tela de listagem compartilhada entre
 * Raio-X (cxray) e ECG. Cada tela fina fornece este objeto (ver common/configs.ts).
 */
export interface DiagnosticsListConfig {
  /** Tipo de exame fixo desta tela */
  examType: ExamType
  /** Título exibido no topo da tela */
  title: string
  /** Caminho base absoluto da listagem (ex.: 'diagnostics/cxray') */
  basePath: string
  /** Chave de sessionStorage usada para restaurar filtros ao voltar do detalhe */
  storageKey: string
  /** Link do manual de uso (exibe o ícone de ajuda quando presente) */
  manualHref?: string
  /** Texto do tooltip do ícone de ajuda */
  manualTooltip?: string
  /** Ícone exibido no topo da tela (ex.: <span className='material-symbols-outlined'>ecg</span>) */
  icon: React.ReactNode
}

export interface DiagnosticsTableProps {
  data: IExamResponse[]
  onRowClick: (exam: IExamResponse) => void
  /** Tipo de exame fixo da listagem (define, p.ex., se há coluna de prévia) */
  examType: ExamType
  /** Caminho base absoluto da listagem, usado para montar o link de detalhe */
  basePath: string
  loading?: boolean
  paginationModel: { page: number; pageSize: number }
  onPaginationChange: (model: { page: number; pageSize: number }) => void
  totalCount: number
  sortModel: Array<{ field: string; sort: 'asc' | 'desc' }>
  onSortModelChange: (
    model: Array<{ field: string; sort: 'asc' | 'desc' }>
  ) => void
  /** True quando há ordenação por coluna ativa (difere do padrão da tela) */
  hasCustomSort?: boolean
  /** Restaura a ordenação padrão da tela */
  onResetSort?: () => void
}

export interface DiagnosticsFiltersProps {
  filters: DiagnosticFilters
  /** Tipo de exame fixo da tela (define subtipo/tipos de resultado exibidos) */
  examType: ExamType
  onFilterChange: (field: keyof DiagnosticFilters, value: any) => void
  onApplyFilters: () => void
  onClearFilters: () => void
}

export type ECGInsights = 'rhythm' | 'diagnostic' | 'form'

export type ECGDiagnosticCode =
  | '1AVB'
  | '2AVB'
  | '3AVB'
  | 'ABQRS'
  | 'AFIB'
  | 'AFLT'
  | 'ALMI'
  | 'AMI'
  | 'ANEUR'
  | 'ASMI'
  | 'BIGU'
  | 'CLBBB'
  | 'CRBBB'
  | 'DIG'
  | 'EL'
  | 'HVOLT'
  | 'ILBBB'
  | 'ILMI'
  | 'IMI'
  | 'INJAL'
  | 'INJAS'
  | 'INJIL'
  | 'INJIN'
  | 'INJLA'
  | 'INVT'
  | 'IPLMI'
  | 'IPMI'
  | 'IRBBB'
  | 'ISCAL'
  | 'ISCAN'
  | 'ISCAS'
  | 'ISCIL'
  | 'ISCIN'
  | 'ISCLA'
  | 'ISC_'
  | 'IVCD'
  | 'LAFB'
  | 'LAO/LAE'
  | 'LMI'
  | 'LNGQT'
  | 'LOWT'
  | 'LPFB'
  | 'LPR'
  | 'LVH'
  | 'LVOLT'
  | 'NDT'
  | 'NORM'
  | 'NST_'
  | 'NT_'
  | 'PAC'
  | 'PACE'
  | 'PMI'
  | 'PRC(S)'
  | 'PSVT'
  | 'PVC'
  | 'QWAVE'
  | 'RAO/RAE'
  | 'RVH'
  | 'SARRH'
  | 'SBRAD'
  | 'SEHYP'
  | 'SR'
  | 'STACH'
  | 'STD_'
  | 'STE_'
  | 'SVARR'
  | 'SVTAC'
  | 'TAB_'
  | 'TRIGU'
  | 'VCLVH'
  | 'WPW'

export interface IECGDiagnostics {
  name: string
  description: string
  leads: string[]
  insight: ECGInsights[]
  auc: number
  emergency: number
}

export type XRayDiagnosticCode =
  | 'hernia'
  | 'mass'
  | 'emphysema'
  | 'effusion'
  | 'cardiomegaly'
  | 'pneumonia'
  | 'nodule'
  | 'pneumothorax'
  | 'fibrosis'
  | 'edema'
  | 'atelectasis'
  | 'pleural_thickening'
  | 'infiltration'
  | 'consolidation'

export const XRayDiagnosticCodesKeys: XRayDiagnosticCode[] = [
  'hernia',
  'mass',
  'emphysema',
  'effusion',
  'cardiomegaly',
  'pneumonia',
  'nodule',
  'pneumothorax',
  'fibrosis',
  'edema',
  'atelectasis',
  'pleural_thickening',
  'infiltration',
  'consolidation'
]
